import { message } from "antd";
import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import LoadingScreen from "./components/LoadingScreen";
import ProxySideNav from "./components/ProxySideNav";
import ProxyDisplayManager from "./ProxyDisplayManager";
import useCanvasScreenshot from "../../hooks/useCanvasScreenshot";
import { update_session_screenshot } from "../../data/slices/sessions";
import { backOff } from "exponential-backoff";
import { Config } from "../../config";

class WebSocketManager {
  constructor(url, token, onMessage) {
    this.url = url;
    this.token = token;
    this.onMessage = onMessage;
    this.ws = null;
    this.sessionId = null;
  }

  connect(sessionId) {
    if (sessionId) {
      this.sessionId = sessionId;
    }

    const fullUrl = `${this.url}?token=${this.token}`;
    this.ws = new WebSocket(fullUrl);
    this.ws.onopen = () => {
      console.log(`WebSocket connection established for session: ${this.sessionId || "unknown"}`);
    };

    this.ws.onmessage = (event) => {
      try {
        console.log("WebSocket message received:", event.data);
        const data = JSON.parse(event.data);
        this.onMessage(data, this.sessionId);
      } catch (error) {
        console.error("Error parsing WebSocket message:", error);
      }
    };

    this.ws.onerror = (error) => {
      // message.error("WebSocket connection error");
      console.error(`WebSocket error for session ${this.sessionId || "unknown"}:`, error);
    };

    this.ws.onclose = () => {
      console.log(`WebSocket connection closed for session: ${this.sessionId || "unknown"}`);
    };
  }

  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
      this.sessionId = null;
    }
  }
}

const ProxyVNCViewer = () => {
  const { desktopSessions } = useSelector((state) => state.sessions);
  const { data: sessions } = desktopSessions || {};
  const token = localStorage.getItem("gomydesk_token");
  const [podPhase, setPodPhase] = useState("Connecting");
  const [isRunning, setIsRunning] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [progress, setProgress] = useState(0);
  const [loadingStep, setLoadingStep] = useState("Initializing");
  const { session: activeSessionId } = useParams();
  const activeSession = sessions?.find((session) => session.id === activeSessionId);
  const progressInterval = useRef(null);
  const displayManager = useRef(null);
  const [connectionAttempts, setConnectionAttempts] = useState(0);
  const [maxRetries] = useState(15);
  const [isRetrying, setIsRetrying] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (activeSession) {
      // Check if displayManager already exists but belongs to a different session
      if (displayManager.current && displayManager.current.session?.id !== activeSession.id) {
        console.log("Session changed, reconnecting to new session:", activeSession.id);
        displayManager.current.disconnect();
        displayManager.current = null;
      }

      setProgress(0);
      setLoadingStep("Initializing desktop session");
      setIsConnected(false);

      progressInterval.current = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval.current);
            return prev;
          }
          return prev + 1;
        });
      }, 300);

      const url = Config.REACT_APP_API_URL.replace("https", "wss");

      const wsManager = new WebSocketManager(
        `${url}desktops/ws/${activeSession?.orgId || activeSession?.namespace}/${activeSession?.name}/status`,
        token,
        handleWebSocketMessage
      );

      wsManager.connect(activeSession.id);

      return () => {
        if (wsManager) wsManager.disconnect();
        clearInterval(progressInterval.current);
        if (displayManager.current) {
          displayManager.current.disconnect();
        }
        setIsConnected(false);
      };
    }
  }, [activeSession?.id]);

  useEffect(() => {
    // Update loading message based on pod phase
    switch (podPhase) {
      case "Pending":
        setLoadingStep("Preparing your desktop environment");
        setProgress(30);
        break;
      case "ContainerCreating":
        setLoadingStep("Setting up your desktop");
        setProgress(50);
        break;
      case "Running":
        setLoadingStep("Desktop ready, establishing connection");
        setProgress(80);
        break;
      default:
        if (podPhase !== "Connecting") {
          setLoadingStep(`Current status: ${podPhase}`);
        }
    }
  }, [podPhase]);

  // Display connection attempt information in UI
  useEffect(() => {
    if (isRetrying && connectionAttempts > 0) {
      setLoadingStep(`Connection attempt ${connectionAttempts} of ${maxRetries}...`);
    }
  }, [connectionAttempts, isRetrying, maxRetries]);

  const handleWebSocketMessage = (data, sessionId) => {
    // Ensure we're handling messages for the current session
    if (sessionId && activeSession?.id && sessionId !== activeSession.id) {
      console.log(
        `Ignoring message for session ${sessionId}, current session is ${activeSession.id}`
      );
      return;
    }

    if (data) {
      setPodPhase(data.podPhase);
      // TODO: Remove this timeout
      setIsRunning(data.running);
      if (data.running) {
        setProgress(100);
        setLoadingStep("Connection established!");
        clearInterval(progressInterval.current);
        window.Fu?.hideButton?.();
        initVncConnectionWithRetry();
      }
    }
  };

  // Initialize VNC connection with exponential backoff retry mechanism
  const initVncConnectionWithRetry = async () => {
    // Always check if displayManager exists but belongs to a different session
    if (displayManager.current && displayManager.current.session?.id !== activeSession?.id) {
      console.log("Session mismatch detected in retry method, disconnecting old session");
      displayManager.current.disconnect();
      displayManager.current = null;
    } else if (displayManager.current && displayManager.current.connected) {
      console.log("Already connected to the correct session");
      setIsConnected(true);
      return; // Already connected to the correct session
    }

    setIsRetrying(true);
    setConnectionAttempts(0);
    setIsConnected(false);

    try {
      await backOff(
        async () => {
          setConnectionAttempts((prev) => prev + 1);
          const connected = await initVncConnection();

          if (!connected) {
            console.log(`Connection attempt ${connectionAttempts} failed, retrying...`);
            throw new Error("VNC connection failed");
          }

          return connected;
        },
        {
          delayFirstAttempt: false,
          startingDelay: 1000, // Start with 1 second delay
          timeMultiple: 1.5, // Increase delay by 50% each time
          numOfAttempts: maxRetries,
          retry: (e, attemptNumber) => {
            console.log(`Retry ${attemptNumber}/${maxRetries} after error: ${e.message}`);
            // Don't retry if we've reached max retries
            return attemptNumber < maxRetries;
          }
        }
      );

      setIsRetrying(false);
      console.log("VNC connection established successfully");
    } catch (error) {
      setIsRetrying(false);
      setIsConnected(false);
      console.error("Failed to connect after maximum retries:", error);
      message.error(
        `Failed to connect to VNC after ${maxRetries} attempts. Please try refreshing the page.`
      );
      setLoadingStep("Connection failed. Please try refreshing the page.");
    }
  };

  const initVncConnection = () => {
    return new Promise((resolve) => {
      try {
        // Add a check to ensure session matches
        if (displayManager.current && displayManager.current.session?.id !== activeSession?.id) {
          console.log("Session mismatch detected, recreating display manager");
          displayManager.current.disconnect();
          displayManager.current = null;
        }

        displayManager.current = new ProxyDisplayManager({
          url: activeSession?.connectionURL,
          credentials: { username: "kasm_user", password: "password" },
          onConnect: () => {
            setIsConnected(true);
            setLoadingStep("Connection established successfully!");
            resolve(true);
            displayManager.current.setQuality(1);
            displayManager.current.setCompression(9);
          },
          onDisconnect: (clean) => {
            console.log("Disconnected from VNC server", clean ? "(clean)" : "(dirty)");
            setIsConnected(false);
            if (!clean && isRunning) {
              setLoadingStep("Connection lost. Attempting to reconnect...");
              setTimeout(() => {
                displayManager.current = null;
                initVncConnectionWithRetry();
              }, 1000);
            }
            resolve(false);
          },
          onCredentialsRequired: (e) => {
            displayManager.current.rfb.sendCredentials({
              password: "password",
              username: "kasm_user"
            });
          },
          viewOnly: false,
          scaleViewport: true,
          compressionLevel: 2,
          resizeSession: false,
          scalingMode: "scale"
        });

        const initialized = displayManager.current.init("proxy-vnc-view", activeSession);
        if (initialized) {
          const connected = displayManager.current.connect();
          if (!connected) {
            displayManager.current = null;
            setIsConnected(false);
            setLoadingStep("Failed to connect to VNC server");
            resolve(false);
          }
          // NOTE: The actual resolve happens in the onConnect callback
        } else {
          displayManager.current = null;
          setIsConnected(false);
          setLoadingStep("Failed to initialize VNC connection");
          resolve(false);
        }
      } catch (error) {
        console.error("Error initializing VNC connection:", error);
        displayManager.current = null;
        setIsConnected(false);
        setLoadingStep("Error connecting to VNC: " + error.message);
        resolve(false);
      }
    });
  };

  const getVncActions = () => {
    if (!displayManager.current) return {};
    window.Fu?.hideButton?.();

    const actions = displayManager.current.getVncActions();
    return {
      ...actions,
      handleToggleFeedback: () => window.Fu?.toggleModal?.()
    };
  };

  useCanvasScreenshot({
    canvasSelector: "#proxy-vnc-view canvas",
    interval: 2000,
    active: isRunning && isConnected,
    quality: 0.5,
    onScreenshot: (dataUrl) => {
      if (activeSession?.id && isRunning && isConnected) {
        dispatch(
          update_session_screenshot({
            sessionId: activeSession.id,
            screenshotUrl: dataUrl
          })
        );
      }
    }
  });

  // Determine if we should show the loading screen
  const showLoadingScreen = !isRunning || !isConnected;

  // Get connection status message
  const getConnectionStatusMessage = () => {
    if (!isRunning) {
      return loadingStep;
    }
    if (isRetrying) {
      return `Connection attempt ${connectionAttempts} of ${maxRetries}...`;
    }
    if (!isConnected) {
      return "Waiting for connection to be established...";
    }
    return loadingStep;
  };

  return (
    <div className="q-page flex full-screen vnc-viewer proxy-vnc-viewer">
      {displayManager.current && (
        <ProxySideNav
          session={activeSession}
          vncActions={getVncActions()}
          vncStates={{
            ...displayManager.current.getStatus(),
            isConnected
          }}
        />
      )}
      <div
        id="proxy-vnc-view"
        style={{
          width: "100%",
          height: "100%",
          display: isRunning && isConnected ? "block" : "none"
        }}
      ></div>
      {showLoadingScreen && (
        <LoadingScreen
          podPhase={podPhase}
          progress={progress}
          loadingStep={getConnectionStatusMessage()}
          thumbnail={activeSession?.template?.imageIconUrl || activeSession?.imageIconUrl}
          retryInfo={isRetrying ? `Connection attempt ${connectionAttempts}/${maxRetries}` : null}
          connectionStatus={isRunning ? (isConnected ? "connected" : "connecting") : "initializing"}
        />
      )}
    </div>
  );
};

export default ProxyVNCViewer;
